import { QueryClient } from '@tanstack/react-query';

// Create a client with optimized settings
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale time: How long data is considered "fresh"
      staleTime: 5 * 60 * 1000, // 5 minutes for most data
      
      // Cache time: How long data stays in cache after becoming unused
      gcTime: 10 * 60 * 1000, // 10 minutes (was cacheTime in v4)
      
      // Refetch strategies
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchOnMount: true,
      
      // Retry configuration
      retry: (failureCount, error) => {
        // Don't retry 4xx errors
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return false;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
      
      // Retry delay with exponential backoff
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      // Global error handling for mutations
      onError: (error) => {
        console.error('Mutation error:', error);
      },
    },
  },
});

// Cache time configurations - optimized for different data types
// DEVELOPMENT: All stale times set to 10 seconds for consistent cache behavior
// PRODUCTION: Change staleTime values below for production deployment
export const CACHE_TIMES = {
  // Categories - rarely change
  CATEGORIES: {
    staleTime: 10 * 1000, // 10 seconds (development) | PRODUCTION: 30 * 60 * 1000 (30 minutes)
    gcTime: 60 * 60 * 1000, // 1 hour
  },
  
  // Subcategories - change occasionally  
  SUBCATEGORIES: {
    staleTime: 10 * 1000, // 10 seconds (development) | PRODUCTION: 30 * 60 * 1000 (30 minutes)
    gcTime: 60 * 60 * 1000, // 1 hour
  },
  
  // Agents data - moderate frequency updates
  AGENTS: {
    staleTime: 10 * 1000, // 10 seconds (development) | PRODUCTION: 5 * 60 * 1000 (5 minutes)
    gcTime: 30 * 60 * 1000, // 30 minutes
  },
  
  // Individual agent - can be cached longer
  AGENT_DETAIL: {
    staleTime: 10 * 1000, // 10 seconds (development) | PRODUCTION: 15 * 60 * 1000 (15 minutes)
    gcTime: 45 * 60 * 1000, // 45 minutes
  },
  
  // Stats - updates frequently  
  STATS: {
    staleTime: 10 * 1000, // 10 seconds (development) | PRODUCTION: 2 * 60 * 1000 (2 minutes)
    gcTime: 10 * 60 * 1000, // 10 minutes
  },
  
  // Config - rarely changes
  CONFIG: {
    staleTime: 10 * 1000, // 10 seconds (development) | PRODUCTION: 60 * 60 * 1000 (1 hour)
    gcTime: 2 * 60 * 60 * 1000, // 2 hours
  },
  
  // Search results - short cache
  SEARCH: {
    staleTime: 10 * 1000, // 10 seconds (development) | PRODUCTION: 30 * 1000 (30 seconds)
    gcTime: 15 * 60 * 1000, // 15 minutes
  },
};

// Query key factories for consistent cache management
export const queryKeys = {
  all: ['ultralendr'],
  
  categories: () => [...queryKeys.all, 'categories'],
  
  subcategories: () => [...queryKeys.all, 'subcategories'],
  subcategoriesByCategory: (categoryId) => [...queryKeys.subcategories(), categoryId],
  
  agents: () => [...queryKeys.all, 'agents'],
  agentsList: (filters) => [...queryKeys.agents(), 'list', filters],
  agentDetail: (id) => [...queryKeys.agents(), 'detail', id],
  agentsSearch: (query) => [...queryKeys.agents(), 'search', query],
  
  stats: () => [...queryKeys.all, 'stats'],
  
  config: () => [...queryKeys.all, 'config'],
  configSection: (section) => [...queryKeys.config(), section],
  
  techStack: () => [...queryKeys.all, 'techstack'],
  
  recommendations: () => [...queryKeys.all, 'recommendations'],
};

// Cache invalidation helpers
export const invalidateQueries = {
  agents: () => queryClient.invalidateQueries({ queryKey: queryKeys.agents() }),
  categories: () => queryClient.invalidateQueries({ queryKey: queryKeys.categories() }),
  subcategories: () => queryClient.invalidateQueries({ queryKey: queryKeys.subcategories() }),
  stats: () => queryClient.invalidateQueries({ queryKey: queryKeys.stats() }),
  config: () => queryClient.invalidateQueries({ queryKey: queryKeys.config() }),
  all: () => queryClient.invalidateQueries({ queryKey: queryKeys.all }),
};

// Prefetch helpers for performance optimization
export const prefetchQueries = {
  categories: () => queryClient.prefetchQuery({
    queryKey: queryKeys.categories(),
    queryFn: () => fetch(`${process.env.REACT_APP_BACKEND_URL}/api/categories`).then(res => res.json()),
    ...CACHE_TIMES.CATEGORIES,
  }),
  
  agents: (filters = {}) => queryClient.prefetchQuery({
    queryKey: queryKeys.agentsList(filters),
    queryFn: () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value.toString());
      });
      return fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accelerators?${params}`).then(res => res.json());
    },
    ...CACHE_TIMES.AGENTS,
  }),
};